import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, Switch, Divider } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import {
  Chart,
  ArgumentAxis,
  AreaSeries,
  LineSeries,
  Tooltip,
  // PieSeries,
  // Legend,
} from '@devexpress/dx-react-chart-bootstrap4';
import { Animation, EventTracker } from '@devexpress/dx-react-chart';
import { find, get, isEmpty, orderBy } from 'lodash';
import moment from 'moment';
import { line, area, curveStepAfter } from 'd3-shape';
import { emojify } from 'node-emoji';

import { Box, Dialog, Button } from 'components';
import {
  errorMessageHandler,
  formatAmount,
  formatMoney,
  sortByDate,
  getNavTotal,
  getNavTotalLatestDate,
} from 'common/utils/helpers';
import {
  getDealUpdates,
  getInvestorNavTrack,
  getInvestorPortfolio,
  getUpcomingDistributions,
} from 'services';
import { FN } from 'common/types';
import history from 'common/utils/history';
import { RootState } from 'redux-modules/store/rootState';
import ReceiveEntireDistribution from 'views/Distribution/ReceiveEntireDistribution';
import InvestNowDeal from 'views/InvestNow';
import { emptyInvestmentDataState } from 'redux-modules/investor/Actions';

import styles from './styles';

// const LegendRoot = (props: any) => (
//   <Legend.Root
//     {...props}
//     style={{
//       display: 'flex',
//       flexDirection: 'row',
//       margin: 'auto',
//       padding: '15px 0',
//       listStyle: 'none',
//     }}
//   />
// );
// const LegendItem = (props: any) => (
//   <Legend.Item
//     {...props}
//     style={{
//       display: 'flex',
//       flexDirection: 'column',
//       padding: '0px 15px',
//       margin: 'auto',
//     }}
//   />
// );
// const LegendLabel = (props: any) => (
//   <Legend.Label {...props} style={{ paddingTop: 3 }} />
// );
const format = () => (tick: any) => tick;

const Area = (props: any) => (
  <AreaSeries.Path
    {...props}
    path={area()
      .x(({ arg }: any) => arg)
      .y1(({ val }: any) => val)
      .y0(({ startVal }: any) => startVal)
      .curve(curveStepAfter)}
  />
);

const Line = (props: any) => (
  <LineSeries.Path
    {...props}
    path={line()
      .x(({ arg }: any) => arg)
      .y(({ val }: any) => val)
      .curve(curveStepAfter)}
  />
);

const TooltipContent = (props: any) => (
  <Tooltip.Content {...props} text={`${formatAmount(props.text)}`} />
);

type PortfolioSummaryProps = {
  spv: string;
  selectedSummarySPV: string;
  setSelectedSummarySPV: FN;
};

const PortfolioSummary = ({
  spv,
  selectedSummarySPV,
  setSelectedSummarySPV,
}: PortfolioSummaryProps) => {
  const classes = styles();
  const dispatch = useDispatch();

  const { dealList, latestDeal } = useSelector(({ Deals }: RootState) => Deals);
  const { investorDeals } = useSelector(({ Investor }: RootState) => Investor);

  const [selectedNav, setSelectedNav] = React.useState<boolean>(false);
  const [portfolioCardData, setPortfolioCardData] = useState<any>({});
  const [navTrackData, setNavTrackData] = useState<any>([]);
  const [investmentUpdateData, setInvestmentUpdateData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [chartData, setChartData] = useState<any>([]);
  const [targetItem, setTargetItem] = React.useState<any>();
  const [upcomingDistribution, setUpcomingDistribution] = React.useState<any>();

  const [
    receiveDistributionModal,
    setReceiveDistributionModal,
  ] = React.useState<boolean>(false);
  const [
    reinvestDistributionModal,
    setReinvestDistributionModal,
  ] = React.useState<boolean>(false);
  const [reInvestPartial, setReInvestPartial] = React.useState<boolean>(false);
  const [reInvestSpv, setReInvestSpv] = React.useState<any>(null);
  const [newLatestDeal, setNewLatestDeal] = React.useState<any>([]);

  const setNavChartData = (navTrackDataV2: any, portfolioCardDataV2: any) => {
    setChartData([]);
    if (!isEmpty(navTrackDataV2)) {
      const sorted = navTrackDataV2.sort(sortByDate);
      const navValueBeforeInvest = sorted
        .slice()
        .reverse()
        .find((navTrack: any) => {
          if (
            moment(get(navTrack, 'date')).isBefore(
              get(portfolioCardDataV2, 'investmentDate'),
            )
          )
            return navTrack;
        });
      const newSortedTrack = sorted.filter((navTrack: any) => {
        if (
          !moment(get(navTrack, 'date')).isBefore(
            get(portfolioCardDataV2, 'investmentDate'),
          )
        )
          return navTrack;
      });
      const lastIndex = newSortedTrack.length - 1;
      const data = newSortedTrack.map((item: any) => {
        return {
          month: moment(get(item, 'date')).format('DD MMM YY'),
          nav: parseFloat(get(item, 'nav', 0)),
          tv:
            parseFloat(get(item, 'nav', 0)) +
            parseFloat(get(item, 'distribution', 0)),
        };
      });
      data.unshift.apply(data, [
        {
          month: moment(get(portfolioCardDataV2, 'investmentDate')).format(
            'DD MMM YY',
          ),
          nav: parseFloat(
            get(navValueBeforeInvest, 'nav') ||
              get(portfolioCardDataV2, 'totalInvestments', 0),
          ),
          tv:
            parseFloat(
              get(navValueBeforeInvest, 'nav') ||
                get(portfolioCardDataV2, 'totalInvestments', 0),
            ) + parseFloat(get(navValueBeforeInvest, 'distribution') || 0),
        },
      ]);
      data.push({
        month: moment().format('DD MMM YY'),
        nav: parseFloat(
          get(newSortedTrack, `[${lastIndex}].nav`) ||
            get(navValueBeforeInvest, 'nav') ||
            get(portfolioCardDataV2, 'totalAssetValue', 0),
        ),
        tv:
          parseFloat(
            get(newSortedTrack, `[${lastIndex}].nav`) ||
              get(navValueBeforeInvest, 'nav') ||
              get(portfolioCardDataV2, 'totalAssetValue', 0),
          ) +
          parseFloat(
            get(newSortedTrack, `[${lastIndex}].distribution`) ||
              get(navValueBeforeInvest, 'distribution') ||
              get(portfolioCardDataV2, 'comulativeDistribution', 0),
          ),
      });
      setChartData(data);
    } else {
      setChartData([
        {
          month: moment(get(portfolioCardDataV2, 'investmentDate')).format(
            'DD MMM YY',
          ),
          nav: parseFloat(get(portfolioCardDataV2, 'totalInvestments', 0)),
          tv: parseFloat(get(portfolioCardDataV2, 'totalInvestments', 0)),
        },
        {
          month: moment().format('DD MMM YY'),
          nav: parseFloat(get(portfolioCardDataV2, 'totalAssetValue', 0)),
          tv:
            parseFloat(get(portfolioCardDataV2, 'totalAssetValue', 0)) +
            parseFloat(get(portfolioCardDataV2, 'comulativeDistribution', 0)),
        },
      ]);
    }
  };

  const handleSwitchChange = (event: any) => {
    setSelectedNav(!event.target.checked);
  };

  // const getDealUpdateChartData = (investUpdateDataV2: any) => {
  //   return [{ text: get(investUpdateDataV2, 'title'), value: 1 }];
  // };

  const onConfirmPartialReInvest = () => {
    setReinvestDistributionModal(true);
    setReInvestPartial(true);
    setReceiveDistributionModal(false);
  };

  const refereshUpcomingDistribution = () => {
    setUpcomingDistribution({});
    setIsLoading(true);
    getUpcomingDistributions()
      .then((res: any) => {
        if (
          !isEmpty(get(res, 'spv')) &&
          get(res, 'balance') > 0 &&
          get(res, 'spv.id') === selectedSummarySPV
        ) {
          setUpcomingDistribution(res);
        }
        setIsLoading(false);
      })
      .catch((err: any) => {
        const message: string = errorMessageHandler(err);
        // eslint-disable-next-line no-console
        console.log('ERROR WHILE FETCHING UPCOMING DISTRIBUTION: ', message);
        setIsLoading(false);
      });
  };
  useEffect(() => {
    if (!isEmpty(selectedSummarySPV)) {
      refereshUpcomingDistribution();
    }
  }, [selectedSummarySPV]);

  useEffect(() => {
    if (!isEmpty(selectedSummarySPV)) {
      setIsLoading(true);
      Promise.all([
        getInvestorPortfolio(selectedSummarySPV),
        getInvestorNavTrack(selectedSummarySPV),
      ])
        .then(([portfolioData, trackData]: any) => {
          setPortfolioCardData(portfolioData);
          setNavTrackData(trackData);
          setNavChartData(trackData, portfolioData);
          setIsLoading(false);
        })
        .catch((err: any) => {
          const message: string = errorMessageHandler(err);
          setErrorMessage(message);
          setIsLoading(false);
          setNavTrackData([]);
        });
    }
  }, [selectedSummarySPV]);

  useEffect(() => {
    if (!isEmpty(selectedSummarySPV)) {
      setIsLoading(true);
      Promise.all([getDealUpdates(selectedSummarySPV)])
        .then(([investmentUpdate]: any) => {
          setInvestmentUpdateData(investmentUpdate);
          setIsLoading(false);
        })
        .catch((err: any) => {
          const message: string = errorMessageHandler(err);
          setErrorMessage(`Investment Update ERROR: ${message}`);
          setIsLoading(false);
        });
    }
  }, [selectedSummarySPV]);

  useEffect(() => {
    console.log(dealList, investorDeals, 'ASD')//eslint-disable-line
    if (!isEmpty(dealList) && !isEmpty(investorDeals)) {
      const filteredDeal = dealList.filter((deal: any) => {
        return !investorDeals.includes(get(deal, 'id')) && !deal.closed;
      });
      const sortedDeal = orderBy(filteredDeal, ['order'], ['asc']);
      console.log( sortedDeal, 'ASD',filteredDeal)//eslint-disable-line

      setNewLatestDeal(sortedDeal.slice(0, 2));
    } else {
      const filteredDeal = dealList.filter((deal: any) => !deal.closed);
      const sortedDeal = orderBy(filteredDeal, ['order'], ['asc']);
      setNewLatestDeal(sortedDeal.slice(0, 2));
    }
  }, [dealList, investorDeals]);

  return (
    <Box className={classes.portfolioSummaryContainer}>
      {isEmpty(selectedSummarySPV) && !isLoading ? (
        <Typography variant="caption" className={classes.spvNotfoundTxt}>
          Deal {spv} SPV not found.
        </Typography>
      ) : (
        <>
          {errorMessage && (
            <Typography variant="body1" className={classes.errorMessage}>
              {errorMessage}
            </Typography>
          )}
          <Box className={classes.mainHeader}>
            <Typography className={classes.mainHeaderText}>
              {' '}
              Investment summary -{' '}
              <Typography
                variant="caption"
                className={classes.mainHeaderSubtext}
              >
                {' '}
                {get(find(dealList, { id: selectedSummarySPV }), 'deal')} SPV
              </Typography>
            </Typography>
            <Typography
              variant="h4"
              className={classes.portfolioBack}
              onClick={() => {
                setSelectedSummarySPV('');
                history.push(`/portfolio`);
              }}
            >
              <ArrowBackIcon className={classes.backIcon} />
              &nbsp;&nbsp;Back to Portfolio
            </Typography>
          </Box>
          {!isEmpty(upcomingDistribution) && (
            <>
              {/* <Box className={classes.rollOverBox}>
                <Typography className={classes.rollOverTxt}>
                  There is an upcoming distribution of
                  {formatAmount(get(upcomingDistribution, 'balance', 0), {
                    maximumFractionDigits: 2,
                  })}{' '}
                  from your investment in{' '}
                  {get(upcomingDistribution, 'spv.deal')} SPV. Please upload
                  your bank account details or re-invest in{' '}
                  {get(latestDeal, 'deal')} SPV by{' '}
                  {moment(get(upcomingDistribution, 'spv.closedAt'))
                    .add(15, 'd')
                    .format('DD MMMM, YYYY')}
                  .
                </Typography>
                <Box className={classes.rollOverButtonBox}>
                  <Button
                    name="Re-invest full amount"
                    className={classes.rollOverButton}
                    onClick={() => {
                      setReinvestDistributionModal(true);
                      setReInvestPartial(false);
                    }}
                  />
                  <Button
                    name="Re-invest partial amount"
                    className={classes.rollOverButton}
                    onClick={() => {
                      setReinvestDistributionModal(true);
                      setReInvestPartial(true);
                    }}
                  />
                  <Button
                    name="Receive entire distribution in cash"
                    className={classes.rollOverButton}
                    onClick={() => setReceiveDistributionModal(true)}
                  />
                </Box>
              </Box> */}
              <Box className={classes.rollOverBox}>
                <Typography className={classes.rollOverTxt}>
                  There is an upcoming rollover from your investment in Bridge
                  to Demo Day SPVs. Please select one of the following to
                  proceed.
                </Typography>
                <Box className={classes.rollOverButtonBox}>
                  <Button
                    name="Roll-over to Bridge to Demo Day SPVs"
                    className={classes.rollOverButton}
                    onClick={() => {
                      setReinvestDistributionModal(true);
                      setReInvestPartial(false);
                      setReInvestSpv(newLatestDeal[0] || latestDeal);
                    }}
                  />
                  <Button
                    name="Roll-over to Continuity SPV"
                    className={classes.rollOverButton}
                    onClick={() => {
                      setReinvestDistributionModal(true);
                      setReInvestPartial(false);
                      setReInvestSpv(newLatestDeal[1] || latestDeal);
                    }}
                  />
                  <Button
                    name="Redeem"
                    className={classes.rollOverButton}
                    onClick={() => setReceiveDistributionModal(true)}
                  />
                </Box>
              </Box>
            </>
          )}
          <Box className={classes.portfolioBody}>
            <Box className={classes.portfHBox}>
              <Box className={classes.portfoBoxs}>
                <Typography className={classes.pBoxText}>Invested</Typography>
                <Typography className={classes.pBoxHText}>
                  {formatAmount(get(portfolioCardData, 'totalInvestments', 0))}
                </Typography>
              </Box>
              <Box className={classes.portfoBoxs}>
                <Typography className={classes.pBoxText}>NAV</Typography>
                {/* <Typography className={classes.pBSText}>
                  As of {moment().format('MMM-YY')}
                </Typography> */}
                <Typography className={classes.pBoxHText}>
                  {' '}
                  {formatAmount(get(portfolioCardData, 'totalAssetValue', 0))}
                </Typography>
              </Box>
              <Box className={classes.portfoBoxs}>
                <Typography className={classes.pBoxText}>
                  Rollover / Distributions
                </Typography>
                <Typography className={classes.pBoxHText}>
                  {formatAmount(
                    get(portfolioCardData, 'comulativeDistribution', 0),
                  )}
                </Typography>
              </Box>
              <Box className={classes.portfoBoxs}>
                <Typography className={classes.pBoxText}>Multiple</Typography>
                <Typography className={classes.pBoxHText}>
                  {parseFloat(get(portfolioCardData, 'multiple') || 0).toFixed(
                    1,
                  )}{' '}
                  X
                </Typography>
              </Box>
            </Box>
            <Box className={classes.portfoGraph}>
              <Box className={classes.graphHeader}>
                <Switch
                  classes={{
                    root: classes.switchRoot,
                    switchBase: classes.switchBase,
                    thumb: classes.thumb,
                    track: classes.track,
                    checked: classes.checked,
                  }}
                  checked={!selectedNav}
                  onChange={handleSwitchChange}
                  name="areaValueType"
                  inputProps={{ 'aria-label': 'secondary checkbox' }}
                />
              </Box>
              <Typography className={classes.graphPHText}>
                <Typography variant="caption" className={classes.gDollorText}>
                  $
                </Typography>
                {formatMoney(
                  getNavTotal(navTrackData, portfolioCardData, selectedNav),
                )}
              </Typography>
              <Typography className={classes.graphPSText}>
                As of{' '}
                {moment(
                  getNavTotalLatestDate(
                    navTrackData,
                    get(portfolioCardData, 'investmentDate'),
                  ),
                ).format('DD MMM, YY')}
              </Typography>
              <Box className={classes.chatGraphBox}>
                {!isEmpty(chartData) && (
                  <Chart data={chartData} height={250}>
                    <ArgumentAxis tickFormat={format} />
                    <LineSeries
                      name="NavLine"
                      valueField={selectedNav ? 'nav' : 'tv'}
                      argumentField="month"
                      color="#322EDC"
                      seriesComponent={Line}
                    />
                    <AreaSeries
                      name="NavArea"
                      valueField={selectedNav ? 'nav' : 'tv'}
                      argumentField="month"
                      color="#322EDC"
                      seriesComponent={Area}
                    />
                    <Animation />
                    <EventTracker />
                    <Tooltip
                      targetItem={targetItem}
                      onTargetItemChange={(targetItem: any) =>
                        setTargetItem(targetItem)
                      }
                      contentComponent={TooltipContent}
                    />
                  </Chart>
                )}
              </Box>
            </Box>
            {!isEmpty(investmentUpdateData) && (
              <Box className={classes.portfoCircleChat}>
                <Typography className={classes.pieChatHTxt}>
                  Investment Updates
                </Typography>
                {investmentUpdateData.map((dealUpdate: any, index: number) => (
                  <>
                    <Box
                      className={classes.pieChatBox}
                      key={get(dealUpdate, 'id')}
                    >
                      <Box className={classes.pieChatLeft}>
                        <Typography className={classes.pieChatLHTxt}>
                          {get(dealUpdate, 'title')}
                        </Typography>
                        <Typography className={classes.piePublishTxt}>
                          Published on{' '}
                          {moment(get(dealUpdate, 'publishedOn')).format(
                            'Do MMM YYYY',
                          )}
                        </Typography>
                        <Typography
                          className={classes.pieChatLTxt}
                          dangerouslySetInnerHTML={{
                            __html: emojify(get(dealUpdate, 'content')),
                          }}
                        ></Typography>
                      </Box>
                      {/* <Box className={classes.pieChatRight}>
                        <Chart
                          data={getDealUpdateChartData(dealUpdate)}
                          height={300}
                        >
                          <Palette
                            scheme={[
                              '#B1B0FE',
                              '#4D87FA',
                              '#0DAAFF',
                              '#0A6DF7',
                            ]}
                          />
                          <PieSeries valueField="value" argumentField="text" />
                          <Legend
                            position="bottom"
                            rootComponent={LegendRoot}
                            itemComponent={LegendItem}
                            labelComponent={LegendLabel}
                          />
                          <Animation />
                        </Chart>
                      </Box> */}
                    </Box>
                    {index !== investmentUpdateData.length - 1 && (
                      <Divider className={classes.dividerLine} />
                    )}
                  </>
                ))}
              </Box>
            )}
          </Box>
        </>
      )}

      <Dialog
        open={receiveDistributionModal}
        maxWidth={'sm'}
        title="Redemption Option"
        buttonText="Submit"
        handleClose={() => {
          setReceiveDistributionModal(false);
        }}
      >
        <ReceiveEntireDistribution
          spv={selectedSummarySPV}
          upcomingDistribution={upcomingDistribution}
          onConfirmPartialReInvest={onConfirmPartialReInvest}
          setReceiveDistributionModal={setReceiveDistributionModal}
          refereshUpcomingDistribution={refereshUpcomingDistribution}
        />
      </Dialog>
      <Dialog
        open={reinvestDistributionModal}
        maxWidth={'lg'}
        title=""
        buttonText="submit"
        className={classes.investPopUp}
        handleClose={() => {
          refereshUpcomingDistribution();
          dispatch(emptyInvestmentDataState());
          setReinvestDistributionModal(false);
          setReInvestSpv(null);
        }}
      >
        {reinvestDistributionModal && (
          <InvestNowDeal
            spv={get(reInvestSpv, 'id') || ''} //|| get(latestDeal, 'deal')
            deal={reInvestSpv} //|| latestDeal
            isRolloverFromPortfolio={true}
            reInvestPartial={reInvestPartial}
            distributionData={upcomingDistribution}
          />
        )}
      </Dialog>
    </Box>
  );
};
export default PortfolioSummary;
